import { ColumnDef } from "@tanstack/react-table";
import { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { ServerDeletedFilterValue } from "../../shared/ServerListFilters";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import ServerStatusPills from "../../shared/ServerStatusPills";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";

import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { useToast } from "../../hooks/use-toast";
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { formatDisplayDateTime, formatDisplayDate } from "../../utils/dateUtils";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import { patient, Result } from "../../interface/Patient";

import FileUpload from "Pages/uploadFile/Patient/Patient";
import { ScrollArea } from "../../components/ui/scroll-area";

import { Option } from "../../interface/Option";
import PolicyNames from "../Shared/PolicyNames";
import useQaAutomationIds from "../../utils/useQaAutomationIds";


export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  is_active?: boolean | string | null;
  status?: string;
  __inactive?: boolean;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}


// Linked Policy status is independent of Patient status.
const linkedPolicyInactive = (entry: any): boolean => {
  if (!entry || typeof entry !== "object") return false;
  if (entry.__inactive === true || entry.is_deleted === true) return true;
  const active = entry.is_active ?? entry.Is_active ?? entry.active;
  const state = String(entry.status ?? entry.Status ?? "").trim().toLowerCase();
  return active === false || active === 0 || active === "0" ||
    String(active).toLowerCase() === "false" || state === "inactive" || state === "disabled";
};

const policyCatalogueRows = (response: any): any[] => {
  const data = response?.data?.result ?? response?.data?.data ?? response?.data?.policies ?? response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const refreshPolicyCatalogue = async (): Promise<any[]> => {
  const records = new Map<string, any>();
  for (const active of ["true", "false"]) {
    for (let page = 1; page <= 30; page++) {
      const response = await PolicyAxiosInstance.get("getPolicy", {
        params: { page, limit: 100, active, is_deleted: "false" },
      });
      const rows = policyCatalogueRows(response);
      rows.forEach((entry: any) => {
        const id = entry?.id ?? entry?.policyDbId;
        if (id !== undefined && id !== null) records.set(String(id), entry);
      });
      const count = Number(response?.data?.pagination?.totalPages ?? response?.data?.totalPages);
      if ((Number.isFinite(count) && count > 0 && page >= count) || rows.length < 100) break;
    }
  }
  return Array.from(records.values());
};

const withCurrentPolicyStatus = (record: PolicyItem, catalogue: any[]): PolicyItem => {
  const match = catalogue.find((entry: any) =>
    (record.id != null && String(entry.id) === String(record.id)) ||
    (record.Policy_id && String(entry.Policy_id ?? entry.policy_id ?? "") === record.Policy_id)
  );
  if (!match) return record;
  return {
    ...record,
    is_active: match.is_active ?? match.Is_active ?? match.active,
    status: match.status ?? match.Status,
    __inactive: linkedPolicyInactive(match),
  };
};

// ─── Backend response normalization ────────────────────────────────────────────
const normalizePatientPlans = (raw: any): any[] => {
  const list = Array.isArray(raw) ? raw : raw == null ? [] : [raw];

  return list.map((plan: any) => {
    if (plan == null || typeof plan !== "object") {
      return {
        id: undefined,
        Plan_name: String(plan ?? ""),
        plan_name: String(plan ?? ""),
        name: String(plan ?? ""),
      };
    }

    const name =
      plan.Plan_name ??
      plan.plan_name ??
      plan.name ??
      plan.label ??
      "";

    return {
      ...plan,
      id: plan.id ?? plan.plan_id ?? plan.value,
      Plan_name: String(name),
      plan_name: String(name),
      name: String(name),
    };
  });
};

const normalizePatientPolicies = (raw: any): PolicyItem[] => {
  const list = Array.isArray(raw) ? raw : raw == null ? [] : [raw];

  return list
    .filter(Boolean)
    .map((policy: any) => {
      if (typeof policy !== "object") {
        return {
          id: undefined,
          Policy_id: String(policy),
          Policy_name: String(policy),
          plans: [],
        };
      }

      return {
        ...policy,
        id: policy.id ?? policy.policy_id ?? policy.value,
        Policy_id: String(
          policy.Policy_id ??
            policy.policy_id ??
            policy.policyId ??
            "",
        ),
        Policy_name: String(
          policy.Policy_name ??
            policy.policy_name ??
            policy.policyName ??
            policy.name ??
            policy.label ??
            "",
        ),
        plans: normalizePatientPlans(
          policy.plans ??
            policy.Plan ??
            policy.plan ??
            [],
        ),
      };
    });
};

const isPatientActive = (value: any): boolean =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "Active" ||
  value === "active";

const normalizePatientRow = (item: any, fallback?: any): Result => {
  const source = item ?? {};
  const base = fallback ?? {};

  const rawPolicy =
    source.Policy ??
    source.policy ??
    base.Policy ??
    base.policy ??
    null;

  return {
    ...base,
    ...source,

    id: source.id ?? base.id,

    national_id:
      source.National_id ??
      source.national_id ??
      base.National_id ??
      base.national_id,

    member_id:
      source.Member_id ??
      source.member_id ??
      base.Member_id ??
      base.member_id,

    patient_passport:
      source.Patient_passport ??
      source.patient_passport ??
      base.Patient_passport ??
      base.patient_passport ??
      null,

    patient_dob:
      source.Patient_dob ??
      source.patient_dob ??
      base.Patient_dob ??
      base.patient_dob ??
      "",

    gender:
      source.Gender ??
      source.gender ??
      base.Gender ??
      base.gender ??
      "",

    first_name:
      source.First_name ??
      source.first_name ??
      base.First_name ??
      base.first_name ??
      "",

    last_name:
      source.Last_name ??
      source.last_name ??
      base.Last_name ??
      base.last_name ??
      "",

    full_name:
      source.Full_name ??
      source.full_name ??
      base.Full_name ??
      base.full_name ??
      "",

    policy_id:
      source.policy_id ??
      source.Policy_id ??
      base.policy_id ??
      base.Policy_id ??
      null,

    policy: normalizePatientPolicies(rawPolicy),

    total_coverage:
      source.Total_Coverage ??
      source.total_coverage ??
      base.Total_Coverage ??
      base.total_coverage ??
      0,

    current_spending:
      source.Current_spending ??
      source.current_spending ??
      base.Current_spending ??
      base.current_spending ??
      0,

    status: isPatientActive(
      source.Status ??
      source.status ??
      base.Status ??
      base.status,
    )
      ? "Active"
      : "Inactive",

    created_by:
      source.created_by ??
      source.Created_by ??
      source.createdBy ??
      base.created_by ??
      null,

    createdat:
      source.createdat ??
      source.created_at ??
      source.createdAt ??
      base.createdat ??
      base.created_at ??
      null,

    updated_by:
      source.updated_by ??
      source.Updated_by ??
      source.updatedBy ??
      base.updated_by ??
      [],

    updatedat:
      source.updatedat ??
      source.updated_at ??
      source.updatedAt ??
      base.updatedat ??
      base.updated_at ??
      null,

    is_deleted: Boolean(
      source.is_deleted ??
        source.Is_deleted ??
        source.deleted ??
        base.is_deleted ??
        base.Is_deleted ??
        false,
    ),

    deleted_by:
      source.deleted_by ??
      source.Deleted_by ??
      source.deletedBy ??
      base.deleted_by ??
      [],
  } as Result;
};

// ─── Role helpers ─────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};


const toSafeDateInput = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";

  const raw = String(value).trim();
  if (!raw) return "";

  // Already an HTML date input value.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
};

const toSafeDisplayDate = (value: any): string => {
  return formatDisplayDate(value);
};

const toSafeDateTime = (value: any): string => {
  return formatDisplayDateTime(value);
};

const PATIENT_NAME_PATTERN = /^[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*(?:\s+[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*)*$/;
const PATIENT_NAME_FORMAT_ERROR =
  "Invalid name. Only letters are allowed and each word must start with a capital letter.";

const validatePatientName = (value: string, fieldLabel: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return `${fieldLabel} is required.`;
  if (trimmed.length < 3) return "Name must be at least 3 characters long";
  if (!PATIENT_NAME_PATTERN.test(trimmed)) return PATIENT_NAME_FORMAT_ERROR;
  return "";
};

const PatientRelation: React.FC = () => {
  useQaAutomationIds("patient");
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  const [items, setItems] = useState<patient>({ result: [] });
  const [policyCatalogue, setPolicyCatalogue] = useState<any[]>([]);
  const refreshLinkedPolicies = async (): Promise<any[]> => {
    try {
      const latest = await refreshPolicyCatalogue();
      setPolicyCatalogue(latest);
      return latest;
    } catch (error) {
      console.warn("Policy catalogue unavailable; preserving relation status", error);
      return policyCatalogue;
    }
  };
  useEffect(() => { void refreshLinkedPolicies(); }, []);
  const currentPolicies = (policies: any): PolicyItem[] =>
    normalizePatientPolicies(policies).map((entry) => withCurrentPolicyStatus(entry, policyCatalogue));

  const [nationalId, setNationalId] = useState<number | undefined>();
  const [memberId, setMemberId] = useState<number | undefined>();
  const [patientPassport, setPatientPassport] = useState<string | undefined>();
  const [patientDob, setPatientDob] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  const [policy, setPolicy] = useState<PolicyItem | null>(null);
  const [totalCoverage, setTotalCoverage] = useState<number>(0);
  const [identifierType, setIdentifierType] = useState<string>("National ID");
  const [identifierValue, setIdentifierValue] = useState<number | undefined>();
  const [currentSpending, setCurrentSpending] = useState<number>(0);
  const [status, setStatus] = useState<boolean>(true);
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [identifierError, setIdentifierError] = useState("");
  const existingIdentifiers = ["12345", "98765", "55555"];
  const [statusError, setStatusError] = useState<string>("");

  const [filterSearch, setFilterSearch] = useState<string>("");
  const [selectedGender, setSelectedGender] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearPolicy, setClearPolicy] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);

  const handleOpenDialog = (item: Result) => { setSelectedItem(item); };
  const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false);};
  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();
  const hasSelectValue = !!selectedMainOption;

  const [firstError, setFirstError] = useState<string>("");
  const [lastError, setLastError] = useState<string>("");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [dobError, setDobError] = useState("");

  const [totalError, setTotalError] = useState("");
  const [currentError, setCurrentError] = useState("");
  const [genderError, setGenderError] = useState("");
  const [midError, setMidError] = useState("");
  const [ppError, setPpError] = useState("");
  const [identifierGroupError, setIdentifierGroupError] = useState("");

  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceTargetItem, setForceTargetItem] = useState<Result | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPatientDob(value);
    if (!value) { setDobError("Date of Birth is required"); return; }
    const [year, month, day] = value.split("-");
    const formattedDate = `${month}/${day}/${year}`;
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!dateRegex.test(formattedDate)) { setDobError("Date format must be mm/dd/yyyy"); return; }
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate >= today) { setDobError("Date of Birth cannot be in the future"); return; }
    setDobError("");
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFirstName(value);
    setFirstError(validatePatientName(value, "First Name"));
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLastName(value);
    setLastError(validatePatientName(value, "Last Name"));
  };

  const hasPatientIdentifier = (
    national: number | undefined = nationalId,
    member: number | undefined = memberId,
    passport: string | undefined = patientPassport,
  ): boolean => {
    return Boolean(
      (typeof national === "number" && national > 0) ||
      (typeof member === "number" && member > 0) ||
      passport?.trim(),
    );
  };

  const validatePatientIdentifiers = (): boolean => {
    if (!hasPatientIdentifier()) {
      setIdentifierGroupError(
        "At least one patient identifier (National ID, Member ID, or Passport Number) must be provided.",
      );
      return false;
    }

    setIdentifierGroupError("");
    return true;
  };

  const handleIDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();

    if (value === "") {
      setNationalId(undefined);
      setIdentifierError("");
      if (hasPatientIdentifier(undefined, memberId, patientPassport)) {
        setIdentifierGroupError("");
      }
      return;
    }

    if (!/^\d+$/.test(value)) {
      setIdentifierError("Identifier must contain numbers only. Letters or symbols are not allowed.");
      setNationalId(undefined);
      return;
    }

    if (existingIdentifiers.includes(value)) {
      setIdentifierError("This identifier already exists.");
      setNationalId(undefined);
      return;
    }

    setIdentifierError("");
    setNationalId(Number(value));
    setIdentifierGroupError("");
  };

  const handleID2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();

    if (value === "") {
      setMemberId(undefined);
      setMidError("");
      if (hasPatientIdentifier(nationalId, undefined, patientPassport)) {
        setIdentifierGroupError("");
      }
      return;
    }

    if (!/^\d+$/.test(value)) {
      setMidError("Identifier must contain numbers only. Letters or symbols are not allowed.");
      setMemberId(undefined);
      return;
    }

    if (existingIdentifiers.includes(value)) {
      setMidError("This identifier already exists.");
      setMemberId(undefined);
      return;
    }

    setMidError("");
    setMemberId(Number(value));
    setIdentifierGroupError("");
  };

  const handleID3Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const trimmedValue = value.trim();
    const passportRegex = /^[A-Za-z0-9\- ]+$/;

    if (trimmedValue === "") {
      setPatientPassport(undefined);
      setPpError("");
      if (hasPatientIdentifier(nationalId, memberId, undefined)) {
        setIdentifierGroupError("");
      }
      return;
    }

    if (!passportRegex.test(value)) {
      setPpError("Passport may contain only letters, numbers, spaces and hyphens.");
      setPatientPassport(undefined);
      return;
    }

    if (existingIdentifiers.includes(trimmedValue)) {
      setPpError("This identifier already exists.");
      setPatientPassport(undefined);
      return;
    }

    setPpError("");
    setPatientPassport(value);
    setIdentifierGroupError("");
  };

  const handleTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.trim() === "") { setTotalError("Total is required."); return; }
    if (!/^\d+$/.test(value)) { setTotalError("Identifier must contain numbers only. Letters or symbols are not allowed."); return; }
    if (existingIdentifiers.includes(value)) { setTotalError("This identifier already exists."); return; }
    setTotalError("");
    setTotalCoverage(Number(value));
  };

  const handleCurrentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.trim() === "") { setCurrentError("Total is required."); return; }
    if (!/^\d+$/.test(value)) { setCurrentError("Identifier must contain numbers only. Letters or symbols are not allowed."); return; }
    if (existingIdentifiers.includes(value)) { setCurrentError("This identifier already exists."); return; }
    setCurrentError("");
    setCurrentSpending(Number(value));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const clear = () => {
    setNationalId(undefined);
    setMemberId(undefined);
    setPatientPassport(undefined);
    setPatientDob("");
    setGender("");
    setFirstName("");
    setLastName("");
    setFullName("");
    setPolicy(null);
    setClearPolicy(true);
    setTimeout(() => setClearPolicy(false), 100);
    setCurrentSpending(0);
    setTotalCoverage(0);
    setStatus(true);
    setSelectedMainOption(null);
    setIdentifierError("");
    setMidError("");
    setPpError("");
    setIdentifierGroupError("");
  };

  const handleAddClick = () => {
    setEditIndex(null);
    clear();
    setIsDialogOpen(true);
  };

  const fetchPatientDetails = async (
    patientDbId: string | number,
    fallback?: Result,
  ): Promise<Result> => {
    const response = await PatientRelationAxiosInstance.get(`getPatient/${patientDbId}`);

    const rawResult =
      response.data?.result ??
      response.data?.patient ??
      response.data?.data ??
      response.data;

    const rawPatient = Array.isArray(rawResult) ? rawResult[0] : rawResult;

    if (!rawPatient) {
      throw new Error("Patient details were not returned by the backend.");
    }

    return normalizePatientRow(rawPatient, fallback);
  };

  const handleViewClick = async (item: Result) => {
    setShowAllUpdates(false);

    try {
      const details = await fetchPatientDetails(item.id, item);
      const catalog = await refreshLinkedPolicies();
      setSelectedItem({ ...details, policy: normalizePatientPolicies(details.policy).map(p => withCurrentPolicyStatus(p, catalog)) } as Result);
    } catch (error) {
      console.error("Failed to load patient details:", error);
      setSelectedItem(item);
      toast({
        title: "Warning",
        description:
          "Patient details could not be refreshed. Showing the available row data.",
      });
    }
  };

  const handleEditClick = async (item: Result) => {
    try {
      const details = await fetchPatientDetails(item.id, item);
      const catalog = await refreshLinkedPolicies();
      handleEdit({ ...details, policy: normalizePatientPolicies(details.policy).map(p => withCurrentPolicyStatus(p, catalog)) } as Result);
    } catch (error) {
      console.error("Failed to load patient details for edit:", error);
      handleEdit(item);
      toast({
        title: "Warning",
        description:
          "Patient details could not be refreshed. Editing the available row data.",
      });
    }

    setIsDialogOpen(true);
  };

  const handlePoliciesChange = (selectedOptions: PolicyItem[]) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      if (policy && linkedPolicyInactive(policy)) return;
      setPolicy(null);
      return;
    }

    // An inactive linked Policy must never be removed by the selector.
    if (policy && linkedPolicyInactive(policy)) {
      setPolicy(policy);
      return;
    }
    const currentPolicyId = policy?.id;

    const newlySelectedPolicy =
      selectedOptions.find(
        (selectedPolicy) =>
          String(selectedPolicy?.id ?? "") !== String(currentPolicyId ?? "")
      ) ??
      selectedOptions[selectedOptions.length - 1];

    setPolicy(newlySelectedPolicy);
  };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, {
    search: string;
    gender: string;
    status: string;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: {
      search: filterSearch,
      gender: selectedGender,
      status: selectedStatus,
      deleted: filterDeleted,
    },
    fetchPage: ({ page, limit, filters }) =>
      PatientRelationAxiosInstance.get("getPatient", {
        params: {
          page,
          limit,
          is_deleted: filters.deleted,
          // Use the backend's unified patient text search.
          // The `name` filter already searches patient name and identifiers,
          // so do not send the same value again as `patient_identifier`.
          name: filters.search.trim() || undefined,
          gender: filters.gender || undefined,
          active: filters.status === "Active" ? "true" : filters.status === "Inactive" ? "false" : undefined,
        },
      }),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: (item: any) => normalizePatientRow(item),
    sortRows: (rows) => [...rows].sort((a: any, b: any) => Number(b.id) - Number(a.id)),
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({ title: "Error", description: "Failed to fetch Patients", variant: "destructive" });
    },
  });

  const togglePatientStatus = async (item: Result, force = false): Promise<void> => {
    try {
      const currentStatus = item.status === "Active" || item.status === true;

      const policyData = Array.isArray(item.policy) && item.policy.length > 0
        ? [{
            id: item.policy[0].id || 0,
            Policy_id: item.policy[0].Policy_id || '',
            Policy_name: item.policy[0].Policy_name || '',
            plans: item.policy[0].plans || []
          }]
        : [];

      const url = force ? `updatePatient?force=true` : `updatePatient`;

      const updatedItem = {
        National_id: item.national_id,
        Member_id: item.member_id,
        Patient_passport: item.patient_passport,
        Patient_dob: item.patient_dob,
        Gender: item.gender,
        First_name: item.first_name,
        Last_name: item.last_name,
        Full_name: item.full_name,
        Policy: policyData,
        Total_Coverage: item.total_coverage,
        Current_spending: item.current_spending,
        Status: !currentStatus,
        token: token,
        id: item.id,
      };

      const response = await PatientRelationAxiosInstance.put(url, updatedItem);

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceTargetItem(null);
        setForceError("");
        setForceAction(null);
        toast({
          title: "Success",
          description: `Status changed to ${!currentStatus ? "Active" : "Inactive"}.`,
        });
      } else {
        setForceId(item.id);
        setForceTargetItem(item);
        setForceAction("status");
        setForceError(response.data.error || "This patient is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "An unexpected error occurred.";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(item.id);
        setForceTargetItem(item);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  const addItem = async (): Promise<void> => {
    setFirstError("");
    setLastError("");
    setIdentifierError("");
    setTotalError("");
    setCurrentError("");
    setGenderError("");

    const identifiersAreValid = validatePatientIdentifiers();
    const firstNameValidationError = validatePatientName(firstName, "First Name");
    const lastNameValidationError = validatePatientName(lastName, "Last Name");

    setFirstError(firstNameValidationError);
    setLastError(lastNameValidationError);

    if (firstNameValidationError || lastNameValidationError || totalCoverage.toString() === "" || currentSpending.toString() === "") {
      if (totalCoverage.toString() === "0") setTotalError("Total Coverage is required.");
      if (currentSpending.toString() === "0") setCurrentError("Current Spending is required.");
      if (gender.toString() === "") setGenderError("Gender is required.");
      return;
    }

    if (!identifiersAreValid) return;

    if (dobError || identifierError || midError || ppError || totalError || currentError || genderError) return;

    try {
      const policyData = policy ? [{
        id: policy.id || 0,
        Policy_id: policy.Policy_id || '',
        Policy_name: policy.Policy_name || '',
        plans: policy.plans || []
      }] : [];

      const newGroupArray = {
        National_id: nationalId,
        Member_id: memberId,
        Patient_passport: patientPassport,
        Patient_dob: patientDob,
        Gender: gender,
        First_name: firstName,
        Last_name: lastName,
        Full_name: `${firstName} ${lastName}`.trim(),
        Policy: policyData,
        Total_Coverage: totalCoverage,
        Current_spending: currentSpending,
        Status: status,
        token: token,
      };

      const response = await PatientRelationAxiosInstance.post("addNewPatient", newGroupArray);

      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        if (response.data.result?.success === false) {
          toast({ title: "Error", description: response.data.result.error || "Failed to add the patient.", variant: "destructive" });
          return;
        }
        toast({ title: "Success", description: "New Patient Added Successfully." });
      } else {
        const errors = response.data.errors || [];
        const errorMessages = errors.map((error: any) => error.error).join("\n");
        toast({ title: "Error", description: errorMessages || response.data.message || "Failed to add the patient.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    setMemberId(item.member_id);
    setNationalId(item.national_id);
    setPatientPassport(item.patient_passport);
    setPatientDob(toSafeDateInput(item.patient_dob));
    setGender(item.gender);
    setFirstName(item.first_name);
    setLastName(item.last_name);
    setFullName(item.full_name);

    const firstPolicy = Array.isArray(item.policy) && item.policy.length > 0
      ? {
          id: item.policy[0].id || 0,
          Policy_id: item.policy[0].Policy_id || "",
          Policy_name: item.policy[0].Policy_name || "",
          plans: item.policy[0].plans || [],
          is_active: (item.policy[0] as any).is_active,
          status: (item.policy[0] as any).status,
          __inactive: linkedPolicyInactive(item.policy[0]),
        }
      : null;

    setPolicy(firstPolicy);
    setClearPolicy(false);
    setCurrentSpending(item.current_spending);
    setTotalCoverage(item.total_coverage);
    setStatus(item.status === "Active" || item.status === true);
    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    // Update requirement: only Patient Name + Patient ID/Identifier are mandatory.
    // DOB, Gender, Policy, Coverage and Current Spending must not block an update.
    const firstNameValidationError = validatePatientName(firstName, "First Name");
    const lastNameValidationError = validatePatientName(lastName, "Last Name");
    setFirstError(firstNameValidationError);
    setLastError(lastNameValidationError);

    if (firstNameValidationError || lastNameValidationError) return;

    // Patient ID means at least one supported patient identifier.
    // Keep the existing identifier format validation, but no other field is required for Update.
    if (!validatePatientIdentifiers()) return;
    if (identifierError || midError || ppError) return;

    try {
      const policyData = policy ? [{
        id: policy.id || 0,
        Policy_id: policy.Policy_id || '',
        Policy_name: policy.Policy_name || '',
        plans: policy.plans || []
      }] : [];

      const updatedItem = {
        National_id: nationalId,
        Member_id: memberId,
        Patient_passport: patientPassport,
        Patient_dob: patientDob,
        Gender: gender,
        First_name: firstName,
        Last_name: lastName,
        Full_name: `${firstName} ${lastName}`.trim(),
        Policy: policyData,
        Total_Coverage: totalCoverage,
        Current_spending: currentSpending,
        Status: status,
        token: token,
        id: id,
      };

      const response = await PatientRelationAxiosInstance.put(`updatePatient`, updatedItem);

      if (response.data.success) {
        await fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "Patient updated successfully." });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Update failed.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Result) => {
    const rows = ["National_id;Member_id;Patient_passport;Patient_dob;Gender;First_name;Last_name;Full_name;Policy;Total_Coverage;Current_spending;Status"];
    const policyArr = item.policy || [];
    const maxLen = Math.max(1, policyArr.length);
    for (let index = 0; index < maxLen; index++) {
      const policies = index < policyArr.length ? policyArr[index].Policy_name || "" : "";
      rows.push([
        item.national_id || "", item.member_id || "", item.patient_passport || "",
        item.patient_dob || "", item.gender || "", item.first_name || "",
        item.last_name || "", item.full_name || "", policies,
        item.total_coverage || "", item.current_spending || "",
        (item.status === "Active" || item.status === true) ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Patient${item.id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await PatientRelationAxiosInstance.delete(`softDeletePatient/${id}`, {
        data: { token: token },
      });

      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Patient Deleted Successfully.", variant: "success" });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This patient is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete Patient";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await PatientRelationAxiosInstance.patch(`restorePatient/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Patient restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore Patient", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore Patient", variant: "destructive" });
    }
  };

  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deletePatient/${id}?force=true` : `deletePatient/${id}`;
      const response = await PatientRelationAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);
        toast({ title: "Success", description: "Patient Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This patient is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete Patient");
    }
  };

  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await PatientRelationAxiosInstance.delete(`softDeletePatient/${id}?force=true`, {
        data: { token },
      });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);
        toast({ title: "Success", description: "Patient force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete Patient");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete Patient");
    }
  };

  const headerClass = "font-bold text-sm normal-case";

  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-center font-medium">{currentPage * pageSize + row.index + 1}</div>
      ),
    },
    {
      accessorKey: "full_name",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Patient Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("full_name") as string}
          </div>
        );
      },
    },
    {
      id: "identifier",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Patient Identifier{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const identifiers = [
          item.national_id ? { type: "National", value: item.national_id } : null,
          item.member_id ? { type: "Member", value: item.member_id } : null,
          item.patient_passport ? { type: "Passport", value: item.patient_passport } : null,
        ];
        return (
          <div className="flex flex-wrap gap-2 justify-center">
            {identifiers.map((id, index) => id && (
              <span key={index} className={`px-3 py-1 rounded-full text-sm font-medium cursor-default transition-colors duration-200 ${
                id.type === "National" ? "bg-blue-100 text-blue-800 hover:bg-blue-200"
                : id.type === "Member" ? "bg-green-100 text-green-800 hover:bg-green-200"
                : "bg-purple-100 text-purple-800 hover:bg-purple-200"
              }`}>
                {id.type}: {id.value}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "gender",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Gender{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const gender = row.getValue("gender") as string;
        if (!gender) return <div className="text-center"></div>;
        const normalized = gender.toLowerCase();
        let displayGender = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
        if (normalized === "male") displayGender = "Male";
        if (normalized === "female" || normalized === "feamal") displayGender = "Female";
        return <div className="text-center">{displayGender}</div>;
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isActive = item.status === "Active" || item.status === true;
        const isDeleted = (item as any).is_deleted;
        const statusValueStr = isActive ? "active" : "inactive";
        return (
          <div className="flex justify-center">
            <button
              data-testid={`patient-table-btn-status-toggle-${item.id}`}
              data-row-id={item.id}
              data-status={statusValueStr}
              onClick={() => !isDeleted && togglePatientStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : isActive ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "patient_dob",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            DOB{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ getValue }) => (
        <div className="text-center">{toSafeDisplayDate(getValue())}</div>
      ),
    },
    {
      accessorKey: "policy",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Policy{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const policyData = row.getValue("policy") as PolicyItem[] | undefined;
        const linked = currentPolicies(policyData);
        if (linked.length === 0) return <div className="text-center text-gray-400">-</div>;
        const searchTerm = filterSearch.toLowerCase().trim();
        const matches = searchTerm.length >= 2
          ? linked.filter(p => String(p.Policy_name || p.Policy_id).toLowerCase().includes(searchTerm))
          : [];
        const displayed = (matches.length > 0 ? matches : linked).slice(0, 3);
        return (
          <div className="w-full px-2 py-1">
            <div className="flex flex-wrap justify-center gap-1.5">
              {displayed.map((p, index) => {
                const inactive = linkedPolicyInactive(p);
                return (
                  <span key={`${p.id ?? p.Policy_id}-${index}`}
                    title={`${p.Policy_id} - ${p.Policy_name}${inactive ? " (Inactive)" : ""}`}
                    className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-xs font-medium ${inactive ? "bg-gray-200 text-gray-600 border-gray-300" : "bg-blue-100 text-blue-800 border-blue-200"}`}>
                    {p.Policy_name || p.Policy_id}{inactive ? " (Inactive)" : ""}
                  </span>
                );
              })}
            </div>
            <div className="mt-1 text-center text-[10px] text-gray-400">
              {matches.length > 0 ? `${matches.length}/${linked.length}` : `${linked.length} total`}
            </div>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <div className="text-center">
          <Button variant="ghost" className={`${headerClass} w-full justify-center`} onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            View{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex justify-center items-center gap-2">
            <button
              data-testid={`patient-table-btn-view-details-${item.id}`}
              onClick={() => void handleViewClick(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="patient">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900" data-testid="patient-page-title">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-shield-alt"></i>
          </span>
          Patient
        </h5>
      </div>

      <DataTable2
        qaIdPrefix="patient-data-table"
        columns={columns}
        data={items.result}
        searchValue={filterSearch}
        onSearchChange={(value) => {
          setFilterSearch(value);
          setCurrentPage(0);
        }}
        searchPlaceholder="Search..."
        searchHelpText="Patient Name, National ID, Member ID, Passport, Policy ID, or Policy Name."
        addButtonLabel="Add New Patient"
        showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
        canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>
                Gender
              </span>
              <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                {[
                  { value: "", label: "All" },
                  { value: "Male", label: "Male" },
                  { value: "Female", label: "Female" },
                ].map((option) => {
                  const selected = selectedGender === option.value;
                  return (
                    <button
                      key={option.value || "all"}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setSelectedGender(option.value);
                        setCurrentPage(0);
                      }}
                      style={{
                        padding: "6px 12px",
                        fontSize: 13,
                        fontWeight: 500,
                        borderRadius: 9999,
                        border: "none",
                        cursor: "pointer",
                        background: selected ? "#ffffff" : "transparent",
                        color: selected ? "#334155" : "#64748b",
                        boxShadow: selected ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <ServerStatusPills
              value={
                selectedStatus === "Active"
                  ? "true"
                  : selectedStatus === "Inactive"
                    ? "false"
                    : ""
              }
              onChange={(value) => {
                setSelectedStatus(value === "true" ? "Active" : value === "false" ? "Inactive" : "");
                setCurrentPage(0);
              }}
            />

            <ServerDeletedPills
              value={filterDeleted}
              onChange={(value) => {
                setFilterDeleted(value);
                setCurrentPage(0);
              }}
            />
          </div>
        }
      />

      {/* ─── View Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
        <DialogContent
          data-testid="patient-dialog-view-details"
          className="max-w-[680px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18)" }}
        >
          {selectedItem && (
            <>
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "18px 22px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    {selectedItem.full_name?.charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 }}>{selectedItem.full_name}</h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: (selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: (selectedItem.status === "Active" || selectedItem.status === true) ? "#86efac" : "#fca5a5", border: `1px solid ${(selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize: 9 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: "rgba(147,197,253,0.8)", margin: "2px 0 0" }}>Patient ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                  <button onClick={() => setSelectedItem(null)} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>×</button>
                </div>
              </div>

              {/* Body */}
              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Identifiers */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                    <i className="fa-solid fa-id-card" style={{ fontSize: 12, color: "#3b82f6" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Patient identifiers</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {[
                      selectedItem.national_id ? { type: "National ID", value: selectedItem.national_id, bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" } : null,
                      selectedItem.member_id    ? { type: "Member ID",    value: selectedItem.member_id,    bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" } : null,
                      selectedItem.patient_passport ? { type: "Passport", value: selectedItem.patient_passport, bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" } : null,
                    ].filter(Boolean).map((id: any, i) => (
                      <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 8, background: id.bg, border: `1px solid ${id.border}` }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: id.color }}>{id.type}:</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: id.color }}>{id.value}</span>
                      </div>
                    ))}
                    {!selectedItem.national_id && !selectedItem.member_id && !selectedItem.patient_passport && (
                      <span style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>No identifiers</span>
                    )}
                  </div>
                </div>

                {/* Personal info */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                    <i className="fa-solid fa-user" style={{ fontSize: 12, color: "#d97706" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Personal information</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Date of birth", value: toSafeDateInput(selectedItem.patient_dob) || "—", icon: "fa-calendar", color: "#3b82f6" },
                      { label: "Gender", value: selectedItem.gender || "—", icon: "fa-venus-mars", color: "#8b5cf6" },
                    ].map((item, i) => (
                      <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>{item.label}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{item.value}</span>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Status</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600, width: "fit-content", background: (selectedItem.status === "Active" || selectedItem.status === true) ? "#dcfce7" : "#fee2e2", color: (selectedItem.status === "Active" || selectedItem.status === true) ? "#166534" : "#991b1b", border: `1px solid ${(selectedItem.status === "Active" || selectedItem.status === true) ? "#bbf7d0" : "#fecaca"}` }}>
                        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Policies */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                    <i className="fa-solid fa-file-lines" style={{ fontSize: 12, color: "#16a34a" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Linked policies & plans</span>
                  </div>
                  {selectedItem.policy && selectedItem.policy.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {currentPolicies(selectedItem.policy).map((p: any, idx: number) => {
                        const planArr = Array.isArray(p.plans) ? p.plans : (p.plan || []);
                        const planNames = planArr.map((pl: any) => (pl?.Plan_name || pl?.plan_name || pl?.name || "").trim()).filter(Boolean).join(", ");
                        return (
                          <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: linkedPolicyInactive(p) ? "#e5e7eb" : "#f8fafc", border: linkedPolicyInactive(p) ? "1px solid #9ca3af" : "1px solid #e2e8f0" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: linkedPolicyInactive(p) ? "#64748b" : "#2563eb", fontFamily: "monospace", flexShrink: 0 }}>#{p.Policy_id}</span>
                            <span style={{ width: 1, height: 16, background: "#e2e8f0", flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{p.Policy_name || p.name}{linkedPolicyInactive(p) ? " (Inactive)" : ""}</span>
                            {planNames && (
                              <>
                                <span style={{ width: 1, height: 16, background: "#e2e8f0", flexShrink: 0 }} />
                                <span style={{ fontSize: 11, color: "#64748b", fontStyle: "italic" }}>{planNames}</span>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>No linked policies found</span>
                  )}
                </div>

                {/* Financial */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={{ background: "#ecfdf5", borderRadius: 12, border: "1px solid #a7f3d0", padding: "12px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#065f46", display: "block", marginBottom: 4 }}>Total coverage</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: "#065f46" }}>{selectedItem.total_coverage || "0.00"}</span>
                  </div>
                  <div style={{ background: "#fef2f2", borderRadius: 12, border: "1px solid #fecaca", padding: "12px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#991b1b", display: "block", marginBottom: 4 }}>Current spending</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: "#991b1b" }}>{selectedItem.current_spending || "0.00"}</span>
                  </div>
                </div>

                {/* Audit Trail */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    <div>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 6 }}>Created by</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String(resolveUserName((selectedItem as any).created_by) ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 6 }}>Created on</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 12, color: "#3b82f6" }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).createdat ? toSafeDateTime((selectedItem as any).createdat) : "—"}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 6 }}>Last updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 12, color: "#10b981" }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).updatedat ? toSafeDateTime((selectedItem as any).updatedat) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

{Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; user_id?: number; at?: string; date?: string; [key: string]: any }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => {
        const updateDate = entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined);
        return (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {updateDate ? toSafeDateTime(updateDate) : "—"}
          </span>
        </div>
      );})}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: "12px 20px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                <button data-testid="patient-btn-close-details" onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "7px 24px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 13, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
                  Close Details
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Add / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          data-testid={editIndex === null ? "patient-dialog-add-patient" : "patient-dialog-edit-patient"}
          className="max-w-[820px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
          onInteractOutside={(event: any) => {
            const target = event.target as HTMLElement;
            if (
              target.closest(".policy-plan-select__menu") ||
              target.closest(".policy-plan-select__menu-portal") ||
              target.closest(".policy-network-select__menu") ||
              target.closest(".policy-network-select__menu-portal")
            ) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event: any) => {
            const target = event.target as HTMLElement;
            if (
              target.closest(".policy-plan-select__menu") ||
              target.closest(".policy-plan-select__menu-portal") ||
              target.closest(".policy-network-select__menu") ||
              target.closest(".policy-network-select__menu-portal")
            ) {
              event.preventDefault();
            }
          }}
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}
        >
          <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
              </div>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{editIndex === null ? "Add Patient" : "Edit Patient"}</h2>
              </div>
            </div>
          </div>

          <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
            <div style={{ padding: "22px 28px 32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                  <i className="fa-solid fa-user" style={{ fontSize: 11, color: "#3b82f6" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Personal Information</span>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">First Name</Label>
                    <Input
                      data-testid="patient-input-first-name"
                      value={firstName}
                      onChange={handleFirstNameChange}
                      placeholder="First Name"
                      className={`mt-1.5 ${firstError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    />
                    {firstError && <p data-testid="patient-message-error-first-name" className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{firstError}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Last Name</Label>
                    <Input
                      data-testid="patient-input-last-name"
                      value={lastName}
                      onChange={handleLastNameChange}
                      placeholder="Last Name"
                      className={`mt-1.5 ${lastError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    />
                    {lastError && <p data-testid="patient-message-error-last-name" className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{lastError}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Date of Birth</Label>
                    <Input data-testid="patient-input-dob" type="date" value={patientDob} onChange={handleDobChange} className="mt-1.5" />
                    {dobError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{dobError}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Gender</Label>
                    <div data-testid="patient-select-gender-wrapper" style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button data-testid="patient-btn-gender-male" type="button" onClick={() => setGender("male")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: gender === "male" ? "1px solid #2563eb" : "1px solid #e2e8f0", background: gender === "male" ? "#eff6ff" : "#fff", color: gender === "male" ? "#1d4ed8" : "#64748b" }}>Male</button>
                      <button data-testid="patient-btn-gender-female" type="button" onClick={() => setGender("female")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: gender === "female" ? "1px solid #ec4899" : "1px solid #e2e8f0", background: gender === "female" ? "#fdf2f8" : "#fff", color: gender === "female" ? "#be185d" : "#64748b" }}>Female</button>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                  <i className="fa-solid fa-id-card" style={{ fontSize: 11, color: "#9333ea" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Identifiers</span>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
                <p className="mb-3 text-xs text-slate-500">
                  Enter at least one identifier. You may provide National ID, Member ID, Passport, or any combination of them.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">National ID</Label>
                    <Input
                      data-testid="patient-input-national-id"
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter National ID"
                      value={nationalId?.toString() ?? ""}
                      onChange={handleIDChange}
                      className={`mt-1.5 ${identifierError || identifierGroupError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    />
                    {identifierError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{identifierError}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Member ID</Label>
                    <Input
                      data-testid="patient-input-member-id"
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter Member ID"
                      value={memberId?.toString() ?? ""}
                      onChange={handleID2Change}
                      className={`mt-1.5 ${midError || identifierGroupError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    />
                    {midError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{midError}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Passport</Label>
                    <Input
                      data-testid="patient-input-passport"
                      type="text"
                      value={patientPassport ?? ""}
                      onChange={handleID3Change}
                      placeholder="Alphanumeric"
                      className={`mt-1.5 ${ppError || identifierGroupError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    />
                    {ppError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{ppError}</p>}
                  </div>
                </div>
                {identifierGroupError && (
                  <p data-testid="patient-message-error-identifiers" className="text-red-500 text-xs mt-3 flex items-center gap-1">
                    <i className="fa-solid fa-circle-exclamation text-[10px]" />
                    {identifierGroupError}
                  </p>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                  <i className="fa-solid fa-file-shield" style={{ fontSize: 11, color: "#16a34a" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Coverage & Status</span>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <Label className="text-xs font-semibold text-slate-500">Policy</Label>
                <div data-testid="patient-select-policy-wrapper" className="mt-1.5">
                  <PolicyNames
                    onChange={handlePoliciesChange}
                    defaultValue={policy ? [policy] : []}
                    clear={clearPolicy}
                    key={editIndex !== null ? `edit-${editIndex}` : 'add-new'}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Total Coverage</Label>
                    <Input data-testid="patient-input-total-coverage" type="text" value={totalCoverage} onChange={handleTotalChange} placeholder="Total Coverage Amount" className="mt-1.5" />
                    {totalError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{totalError}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Current Spending</Label>
                    <Input data-testid="patient-input-current-spending" type="number" value={currentSpending} onChange={handleCurrentChange} placeholder="Amount Spent To Date" className="mt-1.5" />
                    {currentError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{currentError}</p>}
                  </div>
                </div>

                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                  <Label className="text-xs font-semibold text-slate-500">Status</Label>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
                    <button
                      data-testid="patient-btn-patient-status-active"
                      type="button"
                      onClick={() => setStatus(true)}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: status ? "1px solid #16a34a" : "1px solid #e2e8f0", background: status ? "#f0fdf4" : "#fff", color: status ? "#15803d" : "#64748b" }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
                    </button>
                    <button type="button" onClick={() => setStatus(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !status ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !status ? "#fef2f2" : "#fff", color: !status ? "#b91c1c" : "#64748b" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
                    </button>
                  </div>
                  {statusError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{statusError}</p>}
                </div>
              </div>
            </div>
          </ScrollArea>

          <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
            <Button data-testid="patient-btn-cancel-patient" variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
            <Button
              data-testid={editIndex === null ? "patient-btn-add-patient-submit" : "patient-btn-update-patient-submit"}
              disabled={Boolean(
                validatePatientName(firstName, "First Name") ||
                validatePatientName(lastName, "Last Name") ||
                (editIndex !== null && !hasPatientIdentifier())
              )}
              onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }}
              style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
            >
              <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
              {editIndex === null ? "Add Patient" : "Update Patient"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the patient.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Patient Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button
                      onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status" && forceTargetItem) togglePatientStatus(forceTargetItem, true);
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>
                      Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default PatientRelation;